import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useGameStore } from '@/lib/gameStore';
import { generatePlayerNumbers, getBackgroundState, calculateStressLevel } from '@/lib/gameUtils';
import NumberCard from './NumberCard';
import PlayerAvatar from './PlayerAvatar';
import RoundResults from './RoundResults';
import VoiceActivation from './VoiceActivation';
import { mapDatabaseError } from '@/lib/errorUtils';
import { useSound } from '@/hooks/useSound';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface GameRoomProps {
  onGameEnd: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

export default function GameRoom({ onGameEnd, setMusicIntensity }: GameRoomProps) {
  const { currentRoom, playerId, updateRoom } = useGameStore();
  const [myNumbers, setMyNumbers] = useState<[number, number]>([0, 0]);
  const [hasSelected, setHasSelected] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(3);
  const [showResults, setShowResults] = useState(false);
  const [roundSelections, setRoundSelections] = useState<any[]>([]);
  const [showVoiceActivation, setShowVoiceActivation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastProcessedRound, setLastProcessedRound] = useState(-1);

  // Use ref to prevent stale closure in timer
  const hasSelectedRef = useRef(false);
  const { playSound } = useSound();

  const backgroundState = currentRoom ? getBackgroundState(currentRoom.currentTotal, currentRoom.targetTotal) : 'below';

  // Adjust music intensity based on proximity to target
  useEffect(() => {
    if (!currentRoom) return;
    
    if (showVoiceActivation) {
      setMusicIntensity('critical');
      return;
    }

    const distance = Math.abs(currentRoom.currentTotal - 67);
    
    if (distance <= 5) {
      setMusicIntensity('intense');
    } else if (distance <= 15) {
      setMusicIntensity('medium');
    } else {
      setMusicIntensity('waiting');
    }
  }, [currentRoom?.currentTotal, showVoiceActivation, setMusicIntensity]);

  // Keep ref in sync with state
  useEffect(() => {
    hasSelectedRef.current = hasSelected;
  }, [hasSelected]);

  // Verify room status on mount
  useEffect(() => {
    const verifyGameStatus = async () => {
      if (!currentRoom) return;
      
      const { data: room, error } = await supabase
        .from('rooms')
        .select('status')
        .eq('id', currentRoom.id)
        .single();
      
      if (error || room?.status !== 'playing') {
        console.error('Game room accessed but room not in playing state:', room?.status);
        onGameEnd();
      }
    };
    
    verifyGameStatus();
  }, [currentRoom?.id]);

  // Subscribe to room updates
  useEffect(() => {
    if (!currentRoom) return;

    const channel = supabase
      .channel('room-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${currentRoom.id}`
        },
        (payload) => {
          const newRoom = payload.new as any;
          updateRoom({
            status: newRoom.status,
            currentRound: newRoom.current_round,
            currentTotal: newRoom.current_total,
            targetTotal: newRoom.target_total
          });

          // Check if hit 67
          if (newRoom.current_total === 67 && !showVoiceActivation) {
            setShowVoiceActivation(true);
          }

          // Check if game ended
          if (newRoom.status === 'finished') {
            onGameEnd();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id, showVoiceActivation]);

  // Subscribe to game_history to show results for ALL players (not just race winner)
  useEffect(() => {
    if (!currentRoom) return;

    const channel = supabase
      .channel('game-history-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_history',
          filter: `room_id=eq.${currentRoom.id}`
        },
        async (payload) => {
          const historyEntry = payload.new as any;
          
          // Fetch the actual selections for this round
          const { data: selections } = await supabase
            .from('round_selections')
            .select('*')
            .eq('room_id', currentRoom.id)
            .eq('round_number', historyEntry.round_number);
          
          if (selections) {
            setRoundSelections(selections);
            setShowResults(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id]);

  // Generate numbers for new round
  useEffect(() => {
    if (!currentRoom) return;
    
    const isOvershoot = currentRoom.currentTotal > currentRoom.targetTotal;
    const numbers = generatePlayerNumbers(currentRoom.currentRound, isOvershoot);
    setMyNumbers(numbers);
    setHasSelected(false);
    hasSelectedRef.current = false; // Reset ref too
    setIsProcessing(false); // Reset processing flag for new round
    setTimeRemaining(Math.max(2, 3.5 - currentRoom.currentRound * 0.1)); // Scale down timer
  }, [currentRoom?.currentRound]);

  // Round timer - use ref to prevent stale closure, don't run while showing results
  useEffect(() => {
    if (hasSelected || showResults || !currentRoom || currentRoom.status !== 'playing') return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1 && prev > 0.9) {
          playSound('countdown');
        }
        if (prev <= 0.1) {
          clearInterval(timer);
          // Use ref to check current hasSelected state (prevents stale closure)
          if (!hasSelectedRef.current && myNumbers[0] !== 0) {
            handleNumberSelect(myNumbers[0]);
          }
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [hasSelected, showResults, currentRoom?.status, currentRoom?.currentRound, playSound]);

  const handleNumberSelect = async (number: number) => {
    if (!currentRoom || !playerId || hasSelected) return;
    
    playSound('select');

    setHasSelected(true);

    // Store selection
    const { error } = await supabase.from('round_selections').insert({
      room_id: currentRoom.id,
      player_id: playerId,
      round_number: currentRoom.currentRound,
      selected_value: number,
      actual_value: number
    });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Error storing selection (dev only):', error);
      }
      // Silently fail for better UX - selection will auto-submit on timeout
      return;
    }

    // Check if all players have selected
    const { data: selections } = await supabase
      .from('round_selections')
      .select('*')
      .eq('room_id', currentRoom.id)
      .eq('round_number', currentRoom.currentRound);

    const { data: players } = await supabase
      .from('players')
      .select('*')
      .eq('room_id', currentRoom.id)
      .eq('is_active', true);

    // Guard: Only process if all players selected AND we haven't processed this round yet
    if (selections && players && selections.length === players.length && !isProcessing && currentRoom.currentRound !== lastProcessedRound) {
      setIsProcessing(true);
      setLastProcessedRound(currentRoom.currentRound);
      await processRound(selections);
    }
  };

  const processRound = async (selections: any[]) => {
    if (!currentRoom) return;

    try {
      // Call edge function to process round with elevated privileges
      const { data, error } = await supabase.functions.invoke('process-round', {
        body: {
          roomId: currentRoom.id,
          selections: selections.map(s => ({
            player_id: s.player_id,
            selected_value: s.selected_value
          }))
        }
      });

      if (error) {
        console.error('Error processing round:', error);
        setIsProcessing(false);
        return;
      }

      // If round was already processed by another player, wait for realtime update
      if (data.alreadyProcessed) {
        console.log('Round already processed, waiting for realtime update');
        return;
      }

      // Don't show results locally - let game_history realtime subscription handle it
      // This ensures ALL players see results, not just the race winner

      // Check if voice activation triggered
      if (data.voiceActivation) {
        setShowVoiceActivation(true);
      }
    } catch (error) {
      console.error('Error calling process-round function:', error);
    }
  };

  if (!currentRoom) return null;

  return (
    <>
      <div className={`min-h-screen relative overflow-hidden transition-all duration-500 ${
        backgroundState === 'below' ? 'bg-chaos-red/20' :
        backgroundState === 'above' ? 'bg-chaos-yellow/20' :
        'bg-chaos-green/20'
      }`}>
        {/* Animated background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-chaos-purple/20 animate-pulse" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-screen p-4 md:p-8">
          {/* Header */}
          <motion.div 
            className="flex justify-between items-center mb-8"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <div className="glass-card px-6 py-3 rounded-2xl">
              <div className="text-sm text-muted-foreground font-bold">ROUND</div>
              <div className="text-4xl font-black text-primary">{currentRoom.currentRound}</div>
            </div>

            <motion.div 
              className="glass-card px-8 py-4 rounded-2xl"
              animate={{ scale: timeRemaining < 1 ? [1, 1.1, 1] : 1 }}
              transition={{ duration: 0.5, repeat: timeRemaining < 1 ? Infinity : 0 }}
            >
              <div className="text-6xl font-black text-foreground">
                {timeRemaining.toFixed(1)}s
              </div>
            </motion.div>

            <div className="glass-card px-6 py-3 rounded-2xl">
              <div className="text-sm text-muted-foreground font-bold">TARGET</div>
              <div className="text-4xl font-black text-chaos-green">{currentRoom.targetTotal}</div>
            </div>
          </motion.div>

          {/* Number Cards */}
          <div className="flex-1 flex items-center justify-center gap-12 max-w-6xl mx-auto w-full px-4">
            <motion.div 
              className="flex flex-col items-center gap-4 flex-1 max-w-md"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-2xl font-black text-muted-foreground uppercase tracking-wider">
                Option A
              </div>
              <NumberCard 
                number={myNumbers[0]} 
                onSelect={() => handleNumberSelect(myNumbers[0])}
                disabled={hasSelected}
              />
            </motion.div>
            
            <motion.div 
              className="flex flex-col items-center gap-4 flex-1 max-w-md"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-2xl font-black text-muted-foreground uppercase tracking-wider">
                Option B
              </div>
              <NumberCard 
                number={myNumbers[1]} 
                onSelect={() => handleNumberSelect(myNumbers[1])}
                disabled={hasSelected}
              />
            </motion.div>
          </div>

          {/* Players */}
          <motion.div 
            className="flex justify-center gap-4 flex-wrap"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            {currentRoom.players.map((player) => (
              <div key={player.id} className="flex flex-col items-center gap-2">
                <PlayerAvatar player={player} size="lg" showStress />
                <div className="text-sm font-bold text-foreground">{player.name}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showResults && (
          <RoundResults 
            selections={roundSelections}
            players={currentRoom.players}
            previousTotal={currentRoom.currentTotal - roundSelections.reduce((sum, s) => sum + s.selected_value, 0)}
            newTotal={currentRoom.currentTotal}
            targetTotal={currentRoom.targetTotal}
            onClose={() => {
              setShowResults(false);
              setHasSelected(false);
              hasSelectedRef.current = false;
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showVoiceActivation && (
          <VoiceActivation 
            roomId={currentRoom.id}
            playerId={playerId!}
            players={currentRoom.players}
            onContinue={() => setShowVoiceActivation(false)}
            onWin={() => {
              supabase.from('rooms').update({ status: 'finished' }).eq('id', currentRoom.id);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
